import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { getLandingPreviewDevice } from "../previewDevices";
import { resolveSiteExperienceDefaults } from "../../landingEditor/siteExperienceDefaults";
import {
  ColorSetting,
  EditorGroup,
  SegmentedChoiceSetting,
  SliderSetting,
  PageEditorBackButton,
  PageEditorContextIntro,
  PageEditorShell,
  PageHiddenTarget,
  PagePreviewTarget,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";

function previewProductImage(accent, offset) {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="960" height="640" viewBox="0 0 960 640">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#f4f5f9"/>
          <stop offset="1" stop-color="#d9deea"/>
        </linearGradient>
      </defs>
      <rect width="960" height="640" rx="36" fill="url(#bg)"/>
      <circle cx="${760 - offset}" cy="${120 + offset}" r="96" fill="${accent}" opacity=".18"/>
      <rect x="${270 + offset}" y="110" width="420" height="420" rx="58" fill="#ffffff"/>
      <rect x="${346 + offset}" y="166" width="268" height="228" rx="30" fill="${accent}" opacity=".9"/>
      <rect x="${370 + offset}" y="424" width="220" height="34" rx="17" fill="#19242f" opacity=".78"/>
      <rect x="${415 + offset}" y="477" width="130" height="18" rx="9" fill="#19242f" opacity=".32"/>
    </svg>
  `)}`;
}

const PREVIEW_PRODUCT_IMAGES = Object.freeze([
  previewProductImage("#6a5ae0", 0),
  previewProductImage("#e44e31", 22),
  previewProductImage("#2d8c75", -18),
  previewProductImage("#d39a32", 12),
  previewProductImage("#3f6eb3", -28),
]);

const DESCRIPTION_SAMPLE = [
  "Descripción de ejemplo del producto. El contenido real se administra desde el producto y no desde este editor.",
  "Esta vista sólo controla cómo se presenta la descripción: cuántas líneas se ven antes de expandirla y qué textos usan las acciones Ver más / Ver menos.",
  "El colapso publicado conserva la medición real del contenido para mostrar el botón únicamente cuando hace falta.",
].join("\n\n");

const TARGETS = Object.freeze({
  page: { label: "Página", hint: "Layout, fondo, superficies y colores" },
  navigation: { label: "Navegación", hint: "Breadcrumbs y regreso al catálogo" },
  image: { label: "Imagen", hint: "Presentación de la imagen del producto" },
  description: { label: "Descripción", hint: "Colapso y textos Ver más / Ver menos" },
  capabilities: { label: "Capacidades", hint: "Reserva, modalidad, entrega y datos operativos" },
  stock: { label: "Stock", hint: "Visibilidad del estado de disponibilidad" },
  cart: { label: "Carrito", hint: "Visibilidad de compra rápida" },
  attributes: { label: "Información adicional", hint: "Atributos y título del bloque" },
  whatsapp: { label: "WhatsApp", hint: "Visibilidad de la consulta por WhatsApp" },
});

function targetMeta(targetId) {
  return TARGETS[targetId] || TARGETS.page;
}

function pageValue(form) {
  return form?.catalog?.productPage || {};
}

function normalizedCollapsedLines(page) {
  if (page?.descriptionCollapsedLines == null) return 6;
  const value = Number(page.descriptionCollapsedLines);
  if (!Number.isFinite(value)) return 6;
  return Math.min(12, Math.max(0, Math.round(value)));
}

function ProductPreviewTarget({ targetId, activeTarget, onSelect, children, sx = {} }) {
  return (
    <PagePreviewTarget
      targetId={targetId}
      activeTarget={activeTarget}
      onSelect={onSelect}
      label={targetMeta(targetId).label}
      sx={sx}
    >
      {children}
    </PagePreviewTarget>
  );
}

function HiddenProductTarget({ targetId, activeTarget, onSelect }) {
  return (
    <PageHiddenTarget
      targetId={targetId}
      activeTarget={activeTarget}
      onSelect={onSelect}
      label={targetMeta(targetId).label}
    />
  );
}

function ProductContentChip({ label = "Contenido del producto" }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      sx={{ alignSelf: "flex-start", fontWeight: 800, bgcolor: "rgba(255,255,255,.62)" }}
    />
  );
}

function ProductPagePreview({ form, activeTarget, onSelectTarget, device }) {
  const page = pageValue(form);
  const theme = form?.theme || {};
  const previewDevice = getLandingPreviewDevice(device);
  const isDesktop = previewDevice.id === "desktop";
  const isMobile = previewDevice.id === "mobile";
  const textColor = page.textColor || theme.textColor || "#1A1A2E";
  const mutedText = page.mutedTextColor || theme.mutedTextColor || "#5B5D73";
  const background = page.backgroundColor || theme.backgroundColor || "#EEF0F5";
  const surface = page.surfaceColor || theme.surfaceColor || "#FFFFFF";
  const split = page.layout !== "stacked";
  const splitVisible = split && isDesktop;
  const imageFit = page.imageFit === "cover" ? "cover" : "contain";
  const imageHeight = isMobile ? 380 : (isDesktop ? 620 : 560);
  const collapsedLines = normalizedCollapsedLines(page);

  return (
    <Box
      sx={{
        minHeight: 720,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        bgcolor: background,
        color: textColor,
        px: isDesktop ? 4 : 1.5,
        pt: isDesktop ? 8 : 7,
        pb: 5,
        position: "relative",
      }}
      onClick={() => onSelectTarget("page")}
    >
      <Button
        size="small"
        variant={activeTarget === "page" ? "contained" : "outlined"}
        startIcon={<TuneRoundedIcon />}
        onClick={(event) => {
          event.stopPropagation();
          onSelectTarget("page");
        }}
        sx={{
          position: "absolute",
          zIndex: 8,
          top: 10,
          right: 10,
          bgcolor: activeTarget === "page" ? undefined : "rgba(255,255,255,.94)",
        }}
      >
        Página
      </Button>

      {page.showBreadcrumbs !== false ? (
        <ProductPreviewTarget targetId="navigation" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mb: 2.2, minWidth: 0 }}>
          <Stack data-preview-passive="true" direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap sx={{ color: mutedText, minWidth: 0 }}>
            <Typography variant="body2">{page.homeLabel || form?.navigation?.homeLabel || "Inicio"}</Typography>
            <Typography variant="body2">/</Typography>
            <Typography variant="body2">{page.catalogLabel || form?.navigation?.productsLabel || "Catálogo"}</Typography>
            <Typography variant="body2">/</Typography>
            <Typography variant="body2" sx={{ color: textColor, fontWeight: 800, overflowWrap: "anywhere" }}>Producto de ejemplo</Typography>
          </Stack>
        </ProductPreviewTarget>
      ) : (
        <Box sx={{ mb: 2.2 }}>
          <HiddenProductTarget targetId="navigation" activeTarget={activeTarget} onSelect={onSelectTarget} />
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: splitVisible ? "minmax(0,1.15fr) minmax(360px,.85fr)" : "minmax(0,1fr)",
          gap: isDesktop ? 5 : 3,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        <ProductPreviewTarget targetId="image" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
          <Paper data-preview-passive="true" sx={{ p: isMobile ? 1.3 : 2, bgcolor: surface, borderRadius: 3, minWidth: 0, overflow: "hidden" }}>
            <Box sx={{ position: "relative", bgcolor: "#fff", borderRadius: 2, overflow: "hidden", minWidth: 0 }}>
              <Box
                component="img"
                src={PREVIEW_PRODUCT_IMAGES[0]}
                alt=""
                aria-hidden="true"
                sx={{
                  display: "block",
                  width: "100%",
                  height: imageHeight,
                  objectFit: imageFit,
                  p: isMobile ? 1 : 2,
                }}
              />
              <Stack
                spacing={0.7}
                sx={{
                  position: "absolute",
                  left: 12,
                  bottom: 12,
                  maxWidth: "calc(100% - 24px)",
                  alignItems: "flex-start",
                  pointerEvents: "none",
                }}
              >
                <Chip size="small" label={`Ajuste · ${imageFit}`} sx={{ bgcolor: "rgba(255,255,255,.92)" }} />
                <ProductContentChip />
              </Stack>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                mt: 1.5,
                overflowX: "auto",
                overflowY: "hidden",
                maxWidth: "100%",
                minWidth: 0,
                pb: 0.5,
              }}
            >
              {PREVIEW_PRODUCT_IMAGES.map((image, index) => (
                <Box
                  key={image}
                  sx={{
                    flex: "0 0 auto",
                    width: 74,
                    height: 74,
                    borderRadius: 1.5,
                    border: index === 0 ? "2px solid" : "1px solid",
                    borderColor: index === 0 ? "primary.main" : "divider",
                    bgcolor: "#fff",
                    p: 0.5,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt=""
                    aria-hidden="true"
                    sx={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </Box>
              ))}
            </Stack>
          </Paper>
        </ProductPreviewTarget>

        <Paper sx={{ p: isMobile ? 2.2 : 3.2, bgcolor: surface, borderRadius: 3, minWidth: 0 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5, minWidth: 0 }}>
            <Chip label="Producto" color="primary" />
            <Chip label="Categoría" variant="outlined" />
            <ProductContentChip />
          </Stack>
          <Typography variant="h3" sx={{ fontWeight: 950, color: textColor, overflowWrap: "anywhere" }}>Producto de ejemplo</Typography>
          <Typography variant="h4" sx={{ mt: 1.2, color: theme.secondaryColor || "#19242F", fontWeight: 900 }}>$ 25.000</Typography>
          <Divider sx={{ my: 2.2 }} />

          <ProductPreviewTarget targetId="description" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
            <Box data-preview-passive="true" sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.8 }}>
                <Typography variant="caption" sx={{ fontWeight: 900, color: mutedText }}>Descripción</Typography>
                <Chip size="small" variant="outlined" label="Contenido del producto" />
              </Stack>
              <Typography
                sx={{
                  color: mutedText,
                  lineHeight: 1.75,
                  whiteSpace: collapsedLines > 0 ? "normal" : "pre-line",
                  overflowWrap: "anywhere",
                  ...(collapsedLines > 0 ? {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: collapsedLines,
                    overflow: "hidden",
                  } : {}),
                }}
              >
                {DESCRIPTION_SAMPLE}
              </Typography>
              {collapsedLines > 0 ? (
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
                  <Button tabIndex={-1} size="small" sx={{ px: 0, pointerEvents: "none" }}>
                    {page.descriptionMoreLabel || "Ver más"}
                  </Button>
                  <Chip size="small" variant="outlined" label={`${collapsedLines} líneas`} />
                </Stack>
              ) : (
                <Chip size="small" variant="outlined" label="Descripción completa" sx={{ mt: 0.8 }} />
              )}
            </Box>
          </ProductPreviewTarget>

          {page.showCapabilities !== false ? (
            <ProductPreviewTarget targetId="capabilities" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 2.5, minWidth: 0 }}>
              <Paper data-preview-passive="true" variant="outlined" sx={{ p: 1.8, bgcolor: background, borderRadius: 2, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalShippingOutlinedIcon fontSize="small" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }}>Características operativas</Typography>
                    <Typography variant="body2" sx={{ color: mutedText, mt: 0.3 }}>Entrega, reserva o modalidad cuando corresponda.</Typography>
                  </Box>
                </Stack>
              </Paper>
            </ProductPreviewTarget>
          ) : (
            <Box sx={{ mt: 2.5 }}><HiddenProductTarget targetId="capabilities" activeTarget={activeTarget} onSelect={onSelectTarget} /></Box>
          )}

          {page.showStock !== false ? (
            <ProductPreviewTarget targetId="stock" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 2.2 }}>
              <Stack data-preview-passive="true" direction="row" spacing={1} alignItems="center">
                <Inventory2OutlinedIcon fontSize="small" color="success" />
                <Typography sx={{ fontWeight: 900, color: "success.main" }}>8 unidades disponibles</Typography>
              </Stack>
            </ProductPreviewTarget>
          ) : (
            <Box sx={{ mt: 2.2 }}><HiddenProductTarget targetId="stock" activeTarget={activeTarget} onSelect={onSelectTarget} /></Box>
          )}

          {page.showQuickCart !== false ? (
            <ProductPreviewTarget targetId="cart" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 2 }}>
              <Button data-preview-passive="true" tabIndex={-1} fullWidth variant="contained" startIcon={<ShoppingCartRoundedIcon />} sx={{ pointerEvents: "none" }}>
                Agregar al carrito
              </Button>
            </ProductPreviewTarget>
          ) : (
            <Box sx={{ mt: 2 }}><HiddenProductTarget targetId="cart" activeTarget={activeTarget} onSelect={onSelectTarget} /></Box>
          )}

          {page.showAttributes !== false ? (
            <ProductPreviewTarget targetId="attributes" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 2.5 }}>
              <Box data-preview-passive="true">
                <Typography sx={{ fontWeight: 900 }}>{page.additionalInfoTitle || "Información adicional"}</Typography>
                <Typography variant="body2" sx={{ mt: 0.7, color: mutedText }}>Material · Ejemplo</Typography>
              </Box>
            </ProductPreviewTarget>
          ) : (
            <Box sx={{ mt: 2.5 }}><HiddenProductTarget targetId="attributes" activeTarget={activeTarget} onSelect={onSelectTarget} /></Box>
          )}

          {page.showWhatsapp !== false ? (
            <ProductPreviewTarget targetId="whatsapp" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 2 }}>
              <Button data-preview-passive="true" tabIndex={-1} fullWidth variant="outlined" startIcon={<WhatsAppIcon />} sx={{ pointerEvents: "none" }}>
                Consultar por WhatsApp
              </Button>
            </ProductPreviewTarget>
          ) : (
            <Box sx={{ mt: 2 }}><HiddenProductTarget targetId="whatsapp" activeTarget={activeTarget} onSelect={onSelectTarget} /></Box>
          )}
        </Paper>
      </Box>

      <ProductPreviewTarget targetId="navigation" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 3, display: "inline-block", maxWidth: "100%" }}>
        <Button data-preview-passive="true" tabIndex={-1} startIcon={<ArrowBackRoundedIcon />} sx={{ pointerEvents: "none" }}>
          {page.backLabel || "Volver al catálogo"}
        </Button>
      </ProductPreviewTarget>
    </Box>
  );
}

function NavigationEditor({ page, set, navigation }) {
  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar breadcrumbs" checked={page.showBreadcrumbs} onChange={(value) => set("showBreadcrumbs", value)} />
      {page.showBreadcrumbs !== false ? (
        <>
          <TextSetting label="Inicio" value={page.homeLabel} onChange={(value) => set("homeLabel", value)} helperText={`Vacío = ${navigation?.homeLabel || "Inicio"}.`} />
          <TextSetting label="Catálogo" value={page.catalogLabel} onChange={(value) => set("catalogLabel", value)} helperText={`Vacío = ${navigation?.productsLabel || "Catálogo"}.`} />
        </>
      ) : null}
      <TextSetting label="Volver al catálogo" value={page.backLabel} onChange={(value) => set("backLabel", value)} />
    </Stack>
  );
}

function DescriptionEditor({ page, set }) {
  const lines = normalizedCollapsedLines(page);
  return (
    <Stack spacing={1.5}>
      <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2.3, bgcolor: "rgba(25,36,47,.025)" }}>
        <Stack spacing={0.7}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 900 }}>Contenido del producto</Typography>
            <Chip size="small" label="No se edita acá" variant="outlined" />
          </Stack>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)" }}>
            Acá configurás únicamente cómo se presenta la descripción cargada en cada producto.
          </Typography>
        </Stack>
      </Paper>
      <SliderSetting
        label="Líneas antes de colapsar"
        value={page.descriptionCollapsedLines == null ? 6 : page.descriptionCollapsedLines}
        min={0}
        max={12}
        step={1}
        unit="líneas"
        valueLabelFormatter={(value) => value === 0 ? "Completa" : `${value} líneas`}
        onChange={(value) => set("descriptionCollapsedLines", value)}
        helperText="0 muestra la descripción completa."
      />
      {lines > 0 ? (
        <>
          <TextSetting label="Ver más" value={page.descriptionMoreLabel} onChange={(value) => set("descriptionMoreLabel", value)} />
          <TextSetting label="Ver menos" value={page.descriptionLessLabel} onChange={(value) => set("descriptionLessLabel", value)} />
        </>
      ) : (
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
          Descripción completa: no habrá controles Ver más / Ver menos.
        </Typography>
      )}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Chip size="small" color="primary" variant="outlined" label={lines === 0 ? "Siempre completa" : `${lines} líneas iniciales`} />
        {lines > 0 ? <Chip size="small" variant="outlined" label={`Al expandir: ${page.descriptionLessLabel || "Ver menos"}`} /> : null}
      </Stack>
    </Stack>
  );
}

function FlagEditor({ label, checked, onChange, description }) {
  return (
    <Stack spacing={1.3}>
      <ToggleSetting label={label} checked={checked} onChange={onChange} />
      {description ? (
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>{description}</Typography>
      ) : null}
      <Chip size="small" sx={{ alignSelf: "flex-start" }} color={checked !== false ? "success" : "default"} label={checked !== false ? "Visible" : "Oculto"} />
    </Stack>
  );
}

function AttributesEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar información adicional" checked={page.showAttributes} onChange={(value) => set("showAttributes", value)} />
      <TextSetting label="Título del bloque" value={page.additionalInfoTitle} onChange={(value) => set("additionalInfoTitle", value)} />
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
        Los atributos concretos vienen de cada producto; acá sólo controlás su presentación.
      </Typography>
    </Stack>
  );
}

function ImagePresentationEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2.3, bgcolor: "rgba(25,36,47,.025)" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ImageRoundedIcon color="primary" />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 900 }}>La imagen pertenece al producto</Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)" }}>Este editor sólo decide cómo encajarla en la ficha.</Typography>
          </Box>
        </Stack>
      </Paper>
      <SegmentedChoiceSetting
        label="Ajuste de imagen"
        value={page.imageFit || "contain"}
        onChange={(value) => set("imageFit", value)}
        options={[
          { value: "contain", label: "Ver imagen completa" },
          { value: "cover", label: "Cubrir el espacio" },
        ]}
      />
    </Stack>
  );
}

function PageSurfaceEditor({ page, theme, set }) {
  return (
    <Stack spacing={1.5}>
      <EditorGroup title="Distribución" description="Cambia la estructura de la ficha, no el contenido del producto.">
        <SegmentedChoiceSetting
          label="Layout"
          value={page.layout || "split"}
          onChange={(value) => set("layout", value)}
          options={[
            { value: "split", label: "Imagen + detalle" },
            { value: "stacked", label: "Apilado" },
          ]}
        />
      </EditorGroup>
      <EditorGroup title="Apariencia de la página">
        <ColorSetting label="Fondo" value={page.backgroundColor} fallback={theme?.backgroundColor || "#EEF0F5"} onChange={(value) => set("backgroundColor", value)} />
        <ColorSetting label="Superficies" value={page.surfaceColor} fallback={theme?.surfaceColor || "#FFFFFF"} onChange={(value) => set("surfaceColor", value)} />
        <ColorSetting label="Texto" value={page.textColor} fallback={theme?.textColor || "#1A1A2E"} onChange={(value) => set("textColor", value)} />
        <ColorSetting label="Texto secundario" value={page.mutedTextColor} fallback={theme?.mutedTextColor || "#5B5D73"} onChange={(value) => set("mutedTextColor", value)} />
      </EditorGroup>
    </Stack>
  );
}

export default function ProductPageEditor({ form, device, gastronomicMenuEnabled = false, productsEnabled = true, saving = false, onCommit }) {
  const page = pageValue(form);
  const previewForm = resolveSiteExperienceDefaults(form, {
    gastronomicMenuEnabled,
    productsEnabled,
  });
  const [selectedTarget, setSelectedTarget] = React.useState("page");
  const set = React.useCallback((key, value) => onCommit?.(`catalog.productPage.${key}`, value), [onCommit]);
  const selectedMeta = targetMeta(selectedTarget);

  let editor = null;
  if (selectedTarget === "navigation") {
    editor = <NavigationEditor page={page} set={set} navigation={form?.navigation} />;
  } else if (selectedTarget === "description") {
    editor = <DescriptionEditor page={page} set={set} />;
  } else if (selectedTarget === "image") {
    editor = <ImagePresentationEditor page={page} set={set} />;
  } else if (selectedTarget === "stock") {
    editor = (
      <FlagEditor
        label="Mostrar stock"
        checked={page.showStock}
        onChange={(value) => set("showStock", value)}
        description="Si el valor nunca fue configurado, la ficha lo mantiene visible por compatibilidad."
      />
    );
  } else if (selectedTarget === "capabilities") {
    editor = (
      <FlagEditor
        label="Mostrar capacidades"
        checked={page.showCapabilities}
        onChange={(value) => set("showCapabilities", value)}
        description="Incluye duración, modalidad, reserva, entrega y otros datos operativos cuando existan en el producto."
      />
    );
  } else if (selectedTarget === "attributes") {
    editor = <AttributesEditor page={page} set={set} />;
  } else if (selectedTarget === "cart") {
    editor = (
      <FlagEditor
        label="Mostrar compra rápida / carrito"
        checked={page.showQuickCart}
        onChange={(value) => set("showQuickCart", value)}
        description="La disponibilidad real y las reglas de compra siguen viniendo del producto y de los módulos habilitados."
      />
    );
  } else if (selectedTarget === "whatsapp") {
    editor = (
      <FlagEditor
        label="Mostrar WhatsApp"
        checked={page.showWhatsapp}
        onChange={(value) => set("showWhatsapp", value)}
        description="El número y la URL final siguen usando los datos compartidos del negocio."
      />
    );
  } else {
    editor = <PageSurfaceEditor page={page} theme={form?.theme || {}} set={set} />;
  }

  return (
    <PageEditorShell
      title={`Detalle de producto › ${selectedMeta.label}`}
      description={selectedMeta.hint}
      device={device}
      saving={saving}
      preview={<ProductPagePreview form={previewForm} activeTarget={selectedTarget} onSelectTarget={setSelectedTarget} device={device} />}
    >
      {selectedTarget !== "page" ? (
        <PageEditorBackButton
          onClick={() => setSelectedTarget("page")}
          startIcon={<ArrowBackRoundedIcon />}
        />
      ) : (
        <PageEditorContextIntro>
          Tocá la presentación que querés ajustar. Nombre, precio, descripción e imagen real siguen perteneciendo a cada producto.
        </PageEditorContextIntro>
      )}
      {editor}
    </PageEditorShell>
  );
}
